// User-friendly copy for automation-execution failure codes. The backend
// raises these as `err_*` strings on the workflow execution's `last_error`
// (and per-action log `error_message`); the FE shows them on the underway
// row. Codes sourced from Workflow::Actions::Ats::ExecuteStageAction.
export const AUTOMATION_ERROR_LABELS: Record<string, string> = {
  err_automation_mode_missing:
    'Pick a scheduling mode for this interview automation.',
  err_invalid_automation_mode: 'The selected scheduling mode is not supported.',
  err_invalid_date_range_type: 'The selected date range is not supported.',
  err_no_slots_generated:
    'No interview slots in the selected date range — pick a wider range.',
  err_self_schedule_email_config_missing:
    'Set up the self-schedule email on the interview activity.',
  err_availability_email_config_missing:
    'Set up the availability email on the interview activity.',
  err_availability_link_required_in_email_body:
    'Add {{interview.availability_link}} to the availability email body.',
  err_ai_coordinator_not_enabled_for_job:
    'AI scheduling is turned off for this job.',
  err_ai_no_coordinator_templates:
    'No AI coordinator templates are configured for this job.',
  err_active_session_exists:
    'An AI scheduling session is already running for this candidate.',
  err_no_active_interviewers:
    'All interviewers for this interview are deactivated.',
  err_interview_office_not_found:
    'The onsite office for this interview was deleted.',
  err_interview_duration_missing: 'Set a duration on the interview activity.',
  err_interview_location_missing: 'Set a location on the interview activity.',
  err_sender_mailbox_not_connected:
    'Connect your mailbox to send this automation.',
};

// BE wraps any other model validation as `err_dispatch_invalid: <details>`.
const DISPATCH_INVALID_PREFIX = 'err_dispatch_invalid';

const titleCaseCode = (code: string): string =>
  code
    .replace(/^err_/, '')
    .replace(/_/g, ' ')
    .replace(/\b\w/g, (c) => c.toUpperCase());

// Translate a raw automation error (an `err_*` code, an
// `err_dispatch_invalid: <details>` string, or an already-human BE message)
// into copy for the recruiter. Unknown `err_*` codes title-case as a
// fallback; non-code strings pass through unchanged.
export const formatAutomationError = (raw?: string | null): string => {
  const value = raw?.trim();
  if (!value) return 'Something went wrong running this automation.';

  if (value.startsWith(DISPATCH_INVALID_PREFIX)) {
    const details = value
      .slice(DISPATCH_INVALID_PREFIX.length)
      .replace(/^[:\s]+/, '')
      .trim();
    return details
      ? `Could not start the automation: ${details}`
      : 'Could not start the automation.';
  }

  if (AUTOMATION_ERROR_LABELS[value]) return AUTOMATION_ERROR_LABELS[value];

  // Recognised-shape but unmapped code → title-case; otherwise it is
  // already a human message from the BE, so show it verbatim.
  return /^err_[a-z0-9_]+$/.test(value) ? titleCaseCode(value) : value;
};
